import {
  createMockSettingsAdapters,
  type UiConsultationServiceStatus,
} from '@triedge/staff-settings-ui';
import { executeUserPoolGraphqlWithIdToken } from '../amplifyClient';

type GraphqlPayload<TData> = {
  data?: TData | null;
  errors?: Array<{ message?: string }>;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

const fallbackSettingsAdapters = createMockSettingsAdapters('/school/settings');

const toBoolean = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const toNullableText = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized || null;
};

const normalizePaymentReadinessPayload = (value: unknown): Record<string, unknown> => {
  const readiness = asRecord(value);
  const sola = asRecord(readiness.sola);
  const solaProviderReadiness = asRecord(readiness.solaProviderReadiness);
  const donationsFundingReadiness = asRecord(readiness.donationsFundingReadiness);
  const publicIfieldsKey = toNullableText(
    sola.publicIfieldsKey ?? solaProviderReadiness.publicIfieldsKey,
  );
  return {
    ...readiness,
    sola: {
      ...sola,
      enabled: toBoolean(sola.enabled, toBoolean(solaProviderReadiness.enabled, true)),
      donationsEnabled: toBoolean(
        sola.donationsEnabled,
        toBoolean(solaProviderReadiness.donationsEnabled, true),
      ),
      publicIfieldsKey,
      hasPublicIfieldsKeyConfigured: toBoolean(
        sola.hasPublicIfieldsKeyConfigured,
        toBoolean(solaProviderReadiness.hasPublicIfieldsKeyConfigured, Boolean(publicIfieldsKey)),
      ),
      hasTransactionKeyConfigured: toBoolean(
        sola.hasTransactionKeyConfigured,
        toBoolean(solaProviderReadiness.hasTransactionKeyConfigured, false),
      ),
      hasWebhookConfigured: toBoolean(
        sola.hasWebhookConfigured,
        toBoolean(solaProviderReadiness.hasWebhookConfigured, false),
      ),
      primaryDonationFundingMethod:
        toNullableText(sola.primaryDonationFundingMethod)
        ?? toNullableText(donationsFundingReadiness.oneTimePrimaryFundingMethod)
        ?? 'sola_daf',
      fallbackDonationFundingMethod:
        toNullableText(sola.fallbackDonationFundingMethod)
        ?? toNullableText(donationsFundingReadiness.oneTimeFallbackFundingMethod)
        ?? 'stripe_card',
      terminalEnabled: toBoolean(
        sola.terminalEnabled,
        toBoolean(solaProviderReadiness.terminalEnabled, false),
      ),
    },
  };
};

const requireField = <TField,>(
  payload: GraphqlPayload<Record<string, TField>>,
  fieldName: string,
): TField => {
  const errorMessage = (payload.errors ?? [])
    .map((entry) => String(entry.message ?? '').trim())
    .filter(Boolean)
    .join('; ');
  if (errorMessage) {
    throw new Error(errorMessage);
  }
  const fieldValue = payload.data?.[fieldName];
  if (fieldValue == null) {
    throw new Error(`Expected ${fieldName} in GraphQL response.`);
  }
  return fieldValue;
};

export const getYstnjUiConsultationServiceStatus = async (): Promise<UiConsultationServiceStatus> => {
  try {
    const payload = await executeUserPoolGraphqlWithIdToken<
      GraphqlPayload<{ getUiConsultationServiceStatus: UiConsultationServiceStatus }>
    >({
      query: /* GraphQL */ `
        query YstnjGetUiConsultationServiceStatus {
          getUiConsultationServiceStatus {
            visibility
            availability
            message
            configuredModelLabel
            configuredModelId
            latestRunId
          }
        }
      `,
    });
    return requireField(payload, 'getUiConsultationServiceStatus');
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'unknown runtime failure';
    return {
      visibility: 'hidden',
      availability: 'capability_blocked',
      message: `UI consultation service is unavailable right now: ${detail}`,
      configuredModelLabel: null,
      configuredModelId: null,
      latestRunId: null,
    };
  }
};

export const getYstnjPaymentControlPlaneReadiness = async (
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  try {
    const payload = await executeUserPoolGraphqlWithIdToken<
      GraphqlPayload<{ getPaymentControlPlaneReadiness: unknown }>
    >({
      query: /* GraphQL */ `
        query YstnjGetPaymentControlPlaneReadiness($input: AWSJSON) {
          getPaymentControlPlaneReadiness(input: $input)
        }
      `,
      variables: {
        input: typeof input.input === 'string'
          ? input.input
          : JSON.stringify(input.input ?? {}),
      },
    });
    return normalizePaymentReadinessPayload(
      requireField(payload, 'getPaymentControlPlaneReadiness'),
    );
  } catch (error) {
    const blockedPaymentReadinessBaseline =
      await fallbackSettingsAdapters.paymentControlPlaneAdapter.getReadiness({});
    const detail = error instanceof Error ? error.message : 'unknown runtime failure';
    return {
      ...blockedPaymentReadinessBaseline,
      status: 'not_ready',
      blockingIssueCount: 1,
      warningCount: 0,
      controlPlane: {
        ...blockedPaymentReadinessBaseline.controlPlane,
        defaultStripeAccountId: null,
        updatedBy: 'ystnj-runtime-blocked',
      },
      runtime: {
        ...blockedPaymentReadinessBaseline.runtime,
        stripeSecretMode: 'unknown',
        hasStripeSecretConfigured: false,
        hasWebhookSecretConfigured: false,
        hasSuccessUrlConfigured: false,
        hasCancelUrlConfigured: false,
        hasPortalReturnUrlConfigured: false,
        hasWebhookUrlConfigured: false,
      },
      urls: {
        ...blockedPaymentReadinessBaseline.urls,
        successUrl: null,
        cancelUrl: null,
        portalReturnUrl: null,
        webhookUrl: null,
        manifestUrl: null,
      },
      stripeHostedReadiness: {
        ...blockedPaymentReadinessBaseline.stripeHostedReadiness,
        runtime: {
          ...blockedPaymentReadinessBaseline.stripeHostedReadiness.runtime,
          stripeSecretMode: 'unknown',
          hasStripeSecretConfigured: false,
          hasWebhookSecretConfigured: false,
          hasSuccessUrlConfigured: false,
          hasCancelUrlConfigured: false,
          hasPortalReturnUrlConfigured: false,
          hasWebhookUrlConfigured: false,
        },
        account: {
          ...blockedPaymentReadinessBaseline.stripeHostedReadiness.account,
          exists: false,
          chargesEnabled: false,
          payoutsEnabled: false,
          detailsSubmitted: false,
          displayName: null,
          email: null,
          country: null,
          errorMessage: detail,
        },
      },
      stripeAccount: {
        ...blockedPaymentReadinessBaseline.stripeAccount,
        accountId: null,
        exists: false,
        chargesEnabled: false,
        payoutsEnabled: false,
        detailsSubmitted: false,
        displayName: null,
        email: null,
        country: null,
        errorMessage: detail,
      },
      solaProviderReadiness: {
        ...blockedPaymentReadinessBaseline.solaProviderReadiness,
        enabled: false,
        donationsEnabled: false,
        publicIfieldsKey: null,
        hasPublicIfieldsKeyConfigured: false,
        hasTransactionKeyConfigured: false,
        hasWebhookConfigured: false,
        terminalEnabled: false,
      },
      sola: {
        ...blockedPaymentReadinessBaseline.sola,
        enabled: false,
        donationsEnabled: false,
        publicIfieldsKey: null,
        hasPublicIfieldsKeyConfigured: false,
        hasTransactionKeyConfigured: false,
        hasWebhookConfigured: false,
        terminalEnabled: false,
      },
      banquest: {
        ...blockedPaymentReadinessBaseline.banquest,
        enabled: false,
        webhookId: null,
        webhookPathKey: null,
        webhookActive: false,
        lastWebhookSyncAt: null,
        hasSourceKeyConfigured: false,
        hasSourceKeyPinConfigured: false,
        hasApiCredentialConfigured: false,
        hasWebhookSignatureConfigured: false,
        webhookUrl: null,
      },
      latestMirrorEvent: {
        ...blockedPaymentReadinessBaseline.latestMirrorEvent,
        providerEventId: null,
        eventType: null,
        moduleTarget: null,
        occurredAt: null,
        recordedAt: null,
      },
      issues: [
        {
          code: 'ystnj-payment-control-plane-readiness-unavailable',
          severity: 'error',
          message: `Payment control-plane readiness is unavailable right now: ${detail}`,
        },
      ],
    };
  }
};
